import { mergeDeep } from "@apollo/client/utilities";
import { gql, useEnhancedMutation, useEnhancedQuery } from "@uplift-ltd/apollo";
import { makeUrl } from "@uplift-ltd/strings";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CourseIdType, CourseProgressEnum } from "__generated__/globalTypes";
import Alert from "components/common/Alert";
import { BackButton } from "components/common/Button";
import Container from "components/common/Container";
import Date from "components/common/Date";
import Heading from "components/common/Heading";
import HighlightedContent from "components/common/HighlightedContent";
import HtmlContent from "components/common/HtmlContent";
import Link from "components/common/Link";
import { StripeCheckoutStatus } from "components/common/PurchaseModal/StripeCheckoutStatus";
import CourseBody from "components/courses/CourseBody";
import CourseHeader from "components/courses/CourseHeader";
import { StartCourseMutation, StartCourseVariables } from "components/courses/__generated__/Course";
import { TIME_EXPIRED_COURSES_WORDING } from "constants/site";
import { COURSE_URL, COURSES_URL, MY_COURSES_URL } from "constants/urls";
import { clampProgressStep, getStepIndex } from "helpers/courseProgress";
import { isClientSide } from "helpers/mode";
import { PreviewData } from "helpers/preview";
import { replaceNonBreakingSpaces } from "helpers/strings";
import { usePageMeta } from "hooks/usePageMeta";
import useRouterNavigation from "hooks/useRouterNavigation";
import { useRouterQuery } from "hooks/useRouterQuery";
import { useUserContext } from "hooks/useUserContext";
import ErrorPage from "../common/ErrorPage";
import { ALL_COURSES_SLUG_QUERY, AllCoursesSlugQuery } from "./Catalog/AllCourses";
import CourseCompleted from "./CourseCompleted";
import CourseContent from "./CourseContent";
import CourseProgressStepper from "./CourseProgressStepper";
import ProductSidebar from "./ProductSidebar";
import Quiz from "./Quiz/Quiz";
import {
  CourseViewQueryCourse,
  CourseViewQueryQuery as CourseViewQuery,
  CourseViewQueryVariables,
  CourseViewUserQueryCourse,
  CourseViewUserQueryQuery as CourseViewUserQuery,
  CourseViewUserQueryVariables,
} from "./__generated__/queries";
import { COURSE_VIEW_QUERY, COURSE_VIEW_USER_QUERY } from "./queries";
import { CourseQuiz, ProductIntersection } from "./types";
import styles from "./Course.module.css";

export { ALL_COURSES_SLUG_QUERY, COURSE_VIEW_QUERY };

export type { AllCoursesSlugQuery, CourseViewQuery, CourseViewQueryVariables };

const START_COURSE = gql`
  mutation StartCourse($courseId: ID!) {
    startCourse(input: { clientMutationId: "StartCourse", courseId: $courseId }) {
      success
      message
    }
  }
`;

const LIKERT_INSTRUCTIONS =
  "On a scale of 1 to 5, with 1 being Strongly Disagree and 5 being Strongly Agree, please rate the following.";

const QUIZ_CONFIRMATION_TEXT =
  "I hereby certify that I have reviewed the course materials. I also hereby certify that the work presented in these answers is mine and that I have received no assistance in completing this posttest.";

interface CourseProps {
  preview: boolean;
  previewData: PreviewData;
}

export type CourseParams = {
  slug: string;
  page?: string;
};

type GetVariablesOptions = CourseProps & CourseParams;

export const getVariables = ({ preview, previewData, slug }: GetVariablesOptions) => {
  const coursePreview = preview && previewData?.course;
  // The slug may be the id of an unpublished course
  const isId = Number.isInteger(Number(slug));
  let isSameCourse = false;
  if (coursePreview) {
    isSameCourse = isId ? Number(slug) === coursePreview.id : slug === coursePreview.slug;
  }
  const isDraft = isSameCourse && coursePreview && coursePreview?.status === "draft";

  const id = isDraft && coursePreview ? coursePreview.id : slug;

  return {
    onlyEnabled: !preview,
    preview,
    id: `${id}`,
    idType: isDraft ? CourseIdType.DATABASE_ID : CourseIdType.SLUG,
    skip: !id,
  };
};

interface ArticleProps {
  course: CourseViewQueryCourse & CourseViewUserQueryCourse;
  courseUser: CourseViewUserQueryCourse | null | undefined;
  onCertificateChangeHandler: () => void;
  onDirectPurchaseCompleteHandler: () => void;
}

const Article = ({
  course,
  courseUser,
  onCertificateChangeHandler,
  onDirectPurchaseCompleteHandler,
}: ArticleProps) => {
  const { replace } = useRouterNavigation();
  const { routerQuery } = useRouterQuery<CourseParams>();
  const currentStep = routerQuery.page?.toLocaleUpperCase();

  const [markCourseAsStarted] = useEnhancedMutation<StartCourseMutation, StartCourseVariables>(
    START_COURSE
  );

  useEffect(() => {
    if (course?.id && course.isEnrolled && currentStep !== CourseProgressEnum.OVERVIEW) {
      markCourseAsStarted({
        variables: { courseId: course.id },
      });
    }
  }, [course?.id, course?.isEnrolled, markCourseAsStarted, currentStep]);

  const currentStepIndex = !currentStep ? -1 : getStepIndex(currentStep);
  const currentAllowedStep = useMemo(
    () => clampProgressStep(currentStep, courseUser?.courseProgress, courseUser?.isEnrolled),
    [currentStep, courseUser?.courseProgress, courseUser?.isEnrolled]
  );
  const currentAllowedStepIndex = getStepIndex(currentAllowedStep);
  const maxStepIndex = getStepIndex(courseUser?.courseProgress || currentAllowedStep);

  if (isClientSide()) {
    if (currentStepIndex === -1 || currentStepIndex > currentAllowedStepIndex) {
      replace(
        makeUrl(COURSE_URL, {
          slug: routerQuery.slug,
          page: currentAllowedStep.toLocaleLowerCase(),
        }),
        undefined,
        { shallow: true }
      );
    }
  }

  let backToHref;
  let backToHrefLabel;
  if (currentStep === CourseProgressEnum.OVERVIEW) {
    backToHref = COURSES_URL;
    backToHrefLabel = "Back to Course Catalog";
  } else {
    backToHref = MY_COURSES_URL;
    backToHrefLabel = "Back to My Courses";
  }

  const page = currentAllowedStep;

  return (
    <article className={styles.courseArticleContainer}>
      <div className={styles.courseArticleTextContainer}>
        <BackButton backToHref={backToHref}>{backToHrefLabel}</BackButton>
        <CourseHeader
          title={course.title || ""}
          materials={course.materials}
          authors={course.authors}
          isNew={course.isNew}
          isRecentlyUpdated={course.isRecentlyUpdated}
          date={course.date || ""}
          updatedOn={course.updatedOn}
          showBadges={
            page === CourseProgressEnum.OVERVIEW &&
            (course.courseFields?.displayDateBadges || false)
          }
          showContentDisclaimer={page === CourseProgressEnum.CONTENT}
          courseMaterialsExtraText={course.courseFields?.courseMaterialsExtraText || ""}
        />
        {course.isTimeExpiredForUser && (
          <Alert theme="danger">
            <p className="font-bold pb-8">Course Expired</p>
            <p>{TIME_EXPIRED_COURSES_WORDING}</p>
          </Alert>
        )}
        {page === CourseProgressEnum.OVERVIEW && (
          <>
            <CourseBody
              content={replaceNonBreakingSpaces(course.content || "")}
              seoCollapsibleContent={course.seoCollapsibleContent}
            />
            {course.educationalObjectives || course.syllabus ? (
              <HighlightedContent>
                {course.educationalObjectives ? (
                  <>
                    <Heading tag="h3">Educational Objectives</Heading>
                    <HtmlContent>
                      {replaceNonBreakingSpaces(course.educationalObjectives)}
                    </HtmlContent>
                  </>
                ) : null}
                {course.syllabus ? (
                  <>
                    <Heading tag="h3">Syllabus</Heading>
                    <HtmlContent>{replaceNonBreakingSpaces(course.syllabus)}</HtmlContent>
                  </>
                ) : null}
              </HighlightedContent>
            ) : null}
            <div className={styles.courseMetaData}>
              <div title="Program Code" className={styles.courseProgramCode}>
                {course.courseFields?.programCode}
              </div>
              <div title="Published Date">
                <Date dateString={course.date || ""} />
              </div>
            </div>
          </>
        )}
        {page === CourseProgressEnum.CONTENT && <CourseContent course={course} />}
        {page === CourseProgressEnum.POSTTEST &&
          !course.isTimeExpiredForUser &&
          course.quizzes[0] && (
            <>
              <div className={styles.courseContentLink}>
                <Link
                  href={makeUrl(COURSE_URL, {
                    slug: course?.slug || "",
                    page: CourseProgressEnum.CONTENT.toLocaleLowerCase(),
                  })}
                  target="_blank"
                >
                  View Course Content
                </Link>
              </div>

              <Quiz
                courseUserData={courseUser}
                courseId={course.id}
                quiz={course.quizzes[0] as CourseQuiz}
                hasCompleted={maxStepIndex > getStepIndex(CourseProgressEnum.POSTTEST)}
                instructions=""
                confirmationText={QUIZ_CONFIRMATION_TEXT}
                currentQuiz={CourseProgressEnum.POSTTEST}
                nextStep={CourseProgressEnum.OBJECTIVES_EVALUATION}
                isTimeExpired={course.isTimeExpiredForUser}
              />
            </>
          )}
        {page === CourseProgressEnum.OBJECTIVES_EVALUATION &&
          !course.isTimeExpiredForUser &&
          course.quizzes[1] && (
            <Quiz
              courseUserData={courseUser}
              courseId={course.id}
              quiz={course.quizzes[1] as CourseQuiz}
              hasCompleted={maxStepIndex > getStepIndex(CourseProgressEnum.OBJECTIVES_EVALUATION)}
              instructions={LIKERT_INSTRUCTIONS}
              currentQuiz={CourseProgressEnum.OBJECTIVES_EVALUATION}
              nextStep={CourseProgressEnum.ACTIVITY_EVALUATION}
              isTimeExpired={course.isTimeExpiredForUser}
            />
          )}
        {page === CourseProgressEnum.ACTIVITY_EVALUATION &&
          !course.isTimeExpiredForUser &&
          course.quizzes[2] && (
            <Quiz
              courseUserData={courseUser}
              courseId={course.id}
              quiz={course.quizzes[2] as CourseQuiz}
              nextStep={CourseProgressEnum.COMPLETED}
              hasCompleted={maxStepIndex > getStepIndex(CourseProgressEnum.ACTIVITY_EVALUATION)}
              currentQuiz={CourseProgressEnum.ACTIVITY_EVALUATION}
              instructions={LIKERT_INSTRUCTIONS}
              isTimeExpired={course.isTimeExpiredForUser}
            />
          )}
        {page === CourseProgressEnum.COMPLETED && (
          <CourseCompleted course={course} courseRefetchHandler={onCertificateChangeHandler} />
        )}
      </div>
      <div className={styles.courseSidebarContainer}>
        {course.isEnrolled ? (
          <CourseProgressStepper course={course} page={page} />
        ) : (
          course.product && (
            <ProductSidebar
              product={course.product as ProductIntersection}
              onPurchaseComplete={onDirectPurchaseCompleteHandler}
              isMembersExclusive={course.subscriptionFeatureOptIns?.membersExclusive || false}
            />
          )
        )}
      </div>
    </article>
  );
};

const Course: React.FC<CourseProps> = ({ preview, previewData }) => {
  const { routerQuery } = useRouterQuery<CourseParams>();
  const { currentUser } = useUserContext();
  const [isRedirectingAfterPurchase, setIsRedirectingAfterPurchase] = useState(false);

  const { data: courseData, loading: isCourseLoading } = useEnhancedQuery<
    CourseViewQuery,
    CourseViewQueryVariables
  >(COURSE_VIEW_QUERY, {
    variables: getVariables({ ...routerQuery, preview, previewData }),
  });

  const {
    data: courseUserData,
    loading: isCourseUserLoading,
    refetch: courseUserRefetch,
  } = useEnhancedQuery<CourseViewUserQuery, CourseViewUserQueryVariables>(COURSE_VIEW_USER_QUERY, {
    variables: getVariables({ ...routerQuery, preview, previewData }),
  });

  const isLoading = isCourseLoading || isCourseUserLoading || isRedirectingAfterPurchase;
  const courseCore = courseData?.course;
  const courseUser = courseUserData?.course;
  const course = mergeDeep(courseCore, courseUser);

  usePageMeta(course?.title, course?.seo);

  const onCertificateChangeHandler = () => {
    courseUserRefetch();
  };

  const onPurchaseCompleteHandler = useCallback(() => {
    setIsRedirectingAfterPurchase(true);
  }, []);

  if (!isLoading) {
    if (!course) {
      return <ErrorPage />;
    }

    if (course.isExpired && (!currentUser || !course.isEnrolled)) {
      return (
        <ErrorPage
          errorCode="403"
          message="This course has expired"
          description="If you are enrolled in this course, please login to access it."
        />
      );
    }
  }

  return (
    <Container>
      <StripeCheckoutStatus
        onSuccessHandler={onPurchaseCompleteHandler}
        onSuccessRedirectUrl={MY_COURSES_URL}
        loading={isLoading || !course}
      >
        {course && (
          <Article
            course={course}
            courseUser={courseUser}
            onCertificateChangeHandler={onCertificateChangeHandler}
            onDirectPurchaseCompleteHandler={onPurchaseCompleteHandler}
          />
        )}
      </StripeCheckoutStatus>
    </Container>
  );
};

export default Course;
